import { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/utils';
import { X, Users as UsersIcon, RefreshCw, Loader2, ShieldCheck, ShieldAlert, GraduationCap, Sparkles } from 'lucide-react';
import { listUsers, patchUser, type AdminUserRow } from '@/services/api';

export function UsersAdminPanel() {
  const open = useAppStore((s) => s.usersAdminOpen);
  const setOpen = useAppStore((s) => s.setUsersAdminOpen);
  const me = useAuthStore((s) => s.user);

  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listUsers());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  if (!open) return null;

  const setRole = async (id: string, role: 'admin' | 'user') => {
    setBusyId(id);
    setError(null);
    try {
      const updated = await patchUser(id, { role });
      setRows((r) => r.map((u) => (u.id === updated.id ? updated : u)));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4">
      <div className="w-full max-w-3xl h-full sm:h-auto sm:max-h-[88vh] bg-[#0c0c0e] border-0 sm:border sm:border-white/10 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="h-14 border-b border-white/10 px-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-5 w-5 text-blue-400" />
            <h2 className="text-sm font-semibold text-white">Користувачі системи</h2>
            <span className="text-xs text-white/40">({rows.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
              title="Оновити"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {error && (
          <div className="px-5 py-2 bg-red-500/10 border-b border-red-500/30 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2">
          {!loading && rows.length === 0 && (
            <div className="text-center py-10 text-white/30 text-xs">Користувачів немає.</div>
          )}
          {rows.map((u) => {
            const isMe = u.id === me?.id;
            const isAdmin = u.role === 'admin';
            const initial = (u.fullName?.[0] || u.email[0] || 'U').toUpperCase();
            return (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-500 to-yellow-400 shrink-0 flex items-center justify-center text-sm font-bold text-[#0a0a0c]">
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-white truncate">
                      {u.fullName || u.email}
                    </div>
                    {isMe && <span className="text-[10px] text-blue-300 uppercase tracking-widest">Ви</span>}
                  </div>
                  <div className="text-[11px] text-white/40 truncate">{u.email}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={cn(
                      'inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded',
                      isAdmin ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-white/50'
                    )}>
                      {isAdmin ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                      {isAdmin ? 'Адмін' : 'Користувач'}
                    </span>
                    <span className={cn(
                      'inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded',
                      u.mode === 'pro' ? 'bg-amber-500/15 text-amber-300/80' : 'bg-blue-500/15 text-blue-300/80'
                    )}>
                      {u.mode === 'pro' ? <Sparkles className="w-3 h-3" /> : <GraduationCap className="w-3 h-3" />}
                      {u.mode === 'pro' ? 'Юрист' : 'Новачок'}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                  {isAdmin ? (
                    <button
                      onClick={() => setRole(u.id, 'user')}
                      disabled={busyId === u.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/70 transition-colors disabled:opacity-50"
                    >
                      {busyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldAlert className="w-3 h-3" />}
                      Зняти адміна
                    </button>
                  ) : (
                    <button
                      onClick={() => setRole(u.id, 'admin')}
                      disabled={busyId === u.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/40 text-[11px] text-amber-200 font-medium transition-colors disabled:opacity-50"
                    >
                      {busyId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                      Призначити адміном
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
